import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, ShieldCheck, Mail, MessageSquare } from 'lucide-react';

interface RequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestType: string;
}

export default function BriefingRequestModal({ isOpen, onClose, requestType }: RequestModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate high-end submission
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-theme-bg border border-gold/30 p-8 md:p-12 shadow-2xl"
          >
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mx-auto mb-8 border border-gold/30">
                  <ShieldCheck className="h-8 w-8 text-gold" />
                </div>
                <h2 className="text-2xl font-serif italic mb-4">Request Logged</h2>
                <p className="text-taupe text-sm italic mb-8">
                  Your request for a {requestType} has been securely transmitted. A private concierge will contact you through your registered executive channel.
                </p>
                <button 
                  onClick={onClose}
                  className="btn-outline font-bold"
                >
                  Return to Ecosystem
                </button>
              </div>
            ) : (
              <>
                <button 
                  onClick={onClose}
                  className="absolute top-6 right-6 text-taupe hover:text-gold transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <div className="mb-8">
                  <span className="text-[10px] uppercase tracking-[0.5em] text-gold mb-2 block font-bold">Secure Intake</span>
                  <h2 className="text-3xl font-serif italic mb-4">{requestType}</h2>
                  <p className="text-xs text-taupe uppercase tracking-widest leading-loose">
                    Briefings are strictly confidential and mapped to individual architectural requirements.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-taupe mb-2 block">Context Summary (Brief)</label>
                    <textarea 
                      required
                      className="w-full bg-black/20 border border-theme-border p-4 text-sm text-theme-text font-light italic focus:outline-none focus:border-gold/50 min-h-[120px]"
                      placeholder="e.g., Initial structural audit of a long-term commercial partnership..."
                    ></textarea>
                  </div>
                  <div className="bg-gold/5 border border-gold/10 p-4 flex items-start gap-4">
                    <Mail className="h-4 w-4 text-gold shrink-0 mt-1" />
                    <p className="text-[10px] italic text-taupe leading-relaxed">By submitting, you agree to the NDA and discretion protocols of She Deserves Better.</p>
                  </div>
                  <button 
                    disabled={loading}
                    className="btn-gold w-full flex items-center justify-center space-x-3 h-14"
                  >
                    {loading ? (
                      <span className="animate-pulse">Transmitting Data...</span>
                    ) : (
                      <>
                        <span>Submit Private Request</span>
                        <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
